import type { ApolloCache } from '@apollo/client';

/**
 * Update `isFollowingAuthor` on every cached `Post` entity whose
 * `author.username` matches the given username. This is the cache
 * equivalent of the dropped `UPDATE_AUTHOR_FOLLOW_STATE` Redux reducer
 * (TRIL-195): used by the hovercard's follow/unfollow handlers to keep
 * every visible feed card's follow state in sync after the relationship
 * mutation succeeds.
 *
 * Two-phase to avoid nested `cache.modify` calls inside the field
 * updater: first iterate `getUserFeed.posts` across every cached keyArgs
 * bucket to collect matching post IDs; then update each Post entity.
 *
 * Scope: posts currently in some `getUserFeed` cache bucket. Posts in
 * `getPostById` / `getReplies` / `getThread` caches are NOT touched —
 * they refresh on next refetch. This matches the pre-migration reducer's
 * semantic (which iterated `state.feedPosts`, populated only from
 * `getUserFeed`).
 */
export function updateIsFollowingAuthorForAuthor(
  cache: ApolloCache<unknown>,
  username: string,
  isFollowing: boolean,
): void {
  const matchedIds = new Set<string>();
  cache.modify({
    fields: {
      getUserFeed(existing: any, { readField }: any) {
        if (!existing?.posts) return existing;
        for (const ref of existing.posts) {
          const author = readField('author', ref) as
            | { username?: string | null }
            | null
            | undefined;
          if (author?.username === username) {
            const id = readField('id', ref) as string | undefined;
            if (id) matchedIds.add(id);
          }
        }
        return existing;
      },
    },
  });
  for (const id of matchedIds) {
    cache.modify({
      id: cache.identify({ __typename: 'Post', id }),
      fields: { isFollowingAuthor: () => isFollowing },
    });
  }
}
